import { StationaryBookingScreen } from '@components/BookingFlow/Stationary/StationaryBookingScreen';
import { ApiErrorPageWithTrans } from '@components/Error/ApiErrorPageWithTrans';
import { t } from '@lingui/core/macro';
import { cn } from 'noddi-ui-common';
import { useMobileBookingAvailability } from '../../../../hooks/useMobileBookingAvailability';
import { useStationaryGarages } from '../../../../hooks/useStationaryGarages';
import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';
import { getBookingChoicePhotos } from './bookingChoicePhotos';
import { BookingChoiceSkeleton } from './BookingChoiceSkeleton';
import { HomeDeliveryCard } from './HomeDeliveryCard';
import { ServiceCenterCard } from './ServiceCenterCard';

/**
 * First step of the customer booking flow: choose mobile (home) or stationary (service center).
 * Available for every brand; service centers are loaded for the active brand.
 * Stationary option is only shown when departments have garages; locations are
 * only selectable when they have future capacity slots.
 * Brands without garages get the home-delivery card on this same screen rather
 * than a programmatic redirect, so the step never changes under the user.
 *
 * Home delivery is the promoted path: dark photo card with the page's only
 * filled CTA, above/left of the light service-center card.
 */
export const BookingFlowChoice = () => {
  const { currentBrand } = useCurrentBrand();
  const {
    garageOptions,
    hasStationaryLocations,
    isLoading: isStationaryLoading,
    error: stationaryError
  } = useStationaryGarages();
  const {
    hasFutureCapacity: hasMobileCapacity,
    isLoading: isMobileLoading,
    error: mobileError
  } = useMobileBookingAvailability();
  const photos = getBookingChoicePhotos(currentBrand);
  const isLoading = isStationaryLoading || isMobileLoading;
  const error = stationaryError ?? mobileError;

  if (error) {
    return <ApiErrorPageWithTrans error={error} />;
  }

  // Only render the choice once we know whether stationary exists. Rendering it
  // optimistically while loading showed "visit us or we come to you" and then
  // yanked the user somewhere else when the query resolved.
  const showStationaryCard = hasStationaryLocations;

  return (
    <StationaryBookingScreen
      title={t`Appointment booking`}
      description={
        isLoading
          ? undefined
          : showStationaryCard
            ? t`Choose whether we should come to your home, or if you want to book an appointment at our workshop`
            : t`We come to you — book a home delivery tire change`
      }
      hideBackButton
      wrapperClassName='max-w-5xl'
      hideBottomBar
    >
      {isLoading ? (
        <BookingChoiceSkeleton />
      ) : (
        <div
          className={cn(
            'flex flex-col gap-3',
            showStationaryCard && 'md:grid md:grid-cols-2 md:items-stretch md:gap-6'
          )}
        >
          <HomeDeliveryCard photo={photos?.homeDelivery} isBookable={hasMobileCapacity} />
          {showStationaryCard && <ServiceCenterCard photo={photos?.serviceCenter} garageOptions={garageOptions} />}
        </div>
      )}
    </StationaryBookingScreen>
  );
};
